/**
 * Organisation setup catalogues — grades, cost centres and employee groups (ADR 0022).
 *
 * A separate router from `organisation.contract.ts` (departments, branches, posts) only to keep
 * each file reviewable; both mount under `/organisation` with no path overlap.
 *
 * All of it is org configuration, gated by the existing `org.settings:read` / `org.settings:update`
 * rather than new permissions. Placing a PERSON in a grade or cost centre is not here: that is an
 * effective-dated change to their assignment and goes through `employees.reassign` (or the initial
 * assignment on `employees.create`), which carry `gradeId` / `costCentreId`. The one per-person
 * read here is the placement history, which answers "what grade were they in March".
 *
 * The exception is BULK cost-centre placement (`assignCostCentre` / `unassignCostCentre`): filling a
 * cost centre one profile at a time does not scale, so an admin can put many people in at once. It
 * writes the same dated placement rows a reassignment does and refuses the same cases.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  dateOnlySchema,
  errorBodySchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export const gradeSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  code: z.string(),
  name: z.string(),
  /** Position on the ladder, lower = more junior. Lists are sorted by it. */
  rank: z.number().int(),
  description: z.string().nullable(),
  isActive: z.boolean(),
  /** People whose CURRENT assignment is in this grade. */
  employeeCount: z.number().int().nonnegative(),
});
export type Grade = z.infer<typeof gradeSchema>;

/**
 * An org-customizable offboarding reason ("resignation", "termination", ...) picked when starting
 * an exit (`employees.exit`'s `exitType` field, see employee.contract.ts). Same shape as Grade —
 * flat list, no membership/history — because unlike grade/cost centre this is never assigned to a
 * person ahead of time, only recorded once, at exit.
 */
export const exitTypeCatalogueSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  code: z.string(),
  name: z.string(),
  isActive: z.boolean(),
});
export type ExitTypeOption = z.infer<typeof exitTypeCatalogueSchema>;

export const costCentreSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  code: z.string(),
  name: z.string(),
  /** When set, only that legal entity's employees can be placed in this cost centre. */
  legalEntityId: uuidSchema.nullable(),
  legalEntityName: z.string().nullable(),
  /** The cost centre this one rolls up into, if any. */
  parentId: uuidSchema.nullable(),
  parentName: z.string().nullable(),
  /** The budget owner — approver for `cost_centre_owner` workflow steps. An EMPLOYMENT id. */
  ownerEmploymentId: uuidSchema.nullable(),
  ownerName: z.string().nullable(),
  ownerEmployeeCode: z.string().nullable(),
  isActive: z.boolean(),
  /** People whose CURRENT assignment is in this cost centre. */
  employeeCount: z.number().int().nonnegative(),
});
export type CostCentre = z.infer<typeof costCentreSchema>;

export const businessUnitSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  /** When set, only that legal entity's employees can be placed in this business unit. */
  legalEntityId: uuidSchema.nullable(),
  legalEntityName: z.string().nullable(),
  isActive: z.boolean(),
  /** People whose CURRENT assignment is in this business unit. */
  employeeCount: z.number().int().nonnegative(),
});
export type BusinessUnit = z.infer<typeof businessUnitSchema>;

export const employeeGroupSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  name: z.string(),
  description: z.string().nullable(),
  isActive: z.boolean(),
  memberCount: z.number().int().nonnegative(),
});
export type EmployeeGroup = z.infer<typeof employeeGroupSchema>;

/** A member is an EMPLOYMENT (the job), like a manager or a branch head. */
export const employeeGroupMemberSchema = z.object({
  employmentId: uuidSchema,
  employeeCode: z.string(),
  name: z.string(),
  departmentName: z.string().nullable(),
  designation: z.string().nullable(),
  addedAt: z.string().datetime({ offset: true }),
});
export type EmployeeGroupMember = z.infer<typeof employeeGroupMemberSchema>;

/**
 * One person as the bulk pickers show them. Enough to recognise the right person and to see what
 * their placement is today — not the employee record, and no contact details or pay.
 */
export const employmentMatchSchema = z.object({
  employmentId: uuidSchema,
  employeeCode: z.string(),
  name: z.string(),
  legalEntityName: z.string().nullable(),
  departmentName: z.string().nullable(),
  locationName: z.string().nullable(),
  designation: z.string().nullable(),
  gradeCode: z.string().nullable(),
  costCentreCode: z.string().nullable(),
});
export type EmploymentMatch = z.infer<typeof employmentMatchSchema>;

/** A current member of a cost centre, and since when. */
export const costCentreMemberSchema = z.object({
  employmentId: uuidSchema,
  employeeCode: z.string(),
  name: z.string(),
  departmentName: z.string().nullable(),
  designation: z.string().nullable(),
  /** First day of the placement row that put them here. */
  since: dateOnlySchema,
});
export type CostCentreMember = z.infer<typeof costCentreMemberSchema>;

/**
 * One effective-dated assignment row. Grade and cost centre came first; the job-history tab added
 * the rest. Everything added later is optional/nullable so older consumers keep parsing.
 */
export const placementSchema = z.object({
  validFrom: dateOnlySchema,
  /** Inclusive last day; null for the current row. */
  validTo: dateOnlySchema.nullable(),
  gradeId: uuidSchema.nullable(),
  gradeCode: z.string().nullable(),
  gradeName: z.string().nullable(),
  costCentreId: uuidSchema.nullable(),
  costCentreCode: z.string().nullable(),
  costCentreName: z.string().nullable(),
  reason: z.string().nullable(),
  departmentId: uuidSchema.nullable().optional(),
  departmentName: z.string().nullable().optional(),
  locationId: uuidSchema.nullable().optional(),
  locationName: z.string().nullable().optional(),
  /** The manager's EMPLOYMENT (reporting is a property of the job). */
  managerEmploymentId: uuidSchema.nullable().optional(),
  managerName: z.string().nullable().optional(),
  designation: z.string().nullable().optional(),
  jobRole: z.string().nullable().optional(),
  workType: z.string().nullable().optional(),
  tags: z.array(z.string()).optional(),
  /** Who wrote the row (the Person behind `created_by`); null for seeded / system rows. */
  changedBy: z.object({ personId: uuidSchema, displayName: z.string() }).nullable().optional(),
  /**
   * Keys (see PLACEMENT_FIELDS) whose value differs from the previous, older row. Empty on the
   * first row; `initial` is true there instead. Computed server-side over the FULL history, so an
   * `asOf` read still reports what changed on that row.
   */
  changes: z.array(z.string()).optional(),
  initial: z.boolean().optional(),
});
export type Placement = z.infer<typeof placementSchema>;

/**
 * The job-history field table: ONE list used by the API (to compute `changes`) and the web timeline
 * (to label and render them). To surface a new placement field (e.g. business unit), add its
 * optional column to `placementSchema`, then append one entry here - nothing else changes.
 * `value` is what a person reads; `identity` is what "same or different" compares (an id where the
 * label could collide), defaulting to `value`.
 */
export interface PlacementField {
  key: string;
  label: string;
  value: (p: Placement) => string | null;
  identity?: (p: Placement) => string | null;
}

const clean = (v: string | null | undefined): string | null => (v && v.trim() ? v : null);

export const PLACEMENT_FIELDS: readonly PlacementField[] = [
  {
    key: 'department',
    label: 'Department',
    value: (p) => clean(p.departmentName),
    identity: (p) => p.departmentId ?? clean(p.departmentName),
  },
  {
    key: 'location',
    label: 'Location',
    value: (p) => clean(p.locationName),
    identity: (p) => p.locationId ?? clean(p.locationName),
  },
  {
    key: 'manager',
    label: 'Reporting manager',
    value: (p) => clean(p.managerName),
    identity: (p) => p.managerEmploymentId ?? clean(p.managerName),
  },
  { key: 'designation', label: 'Job title', value: (p) => clean(p.designation) },
  { key: 'jobRole', label: 'Job role', value: (p) => clean(p.jobRole) },
  {
    key: 'grade',
    label: 'Grade',
    value: (p) => clean(p.gradeName) ?? clean(p.gradeCode),
    identity: (p) => p.gradeId ?? clean(p.gradeCode),
  },
  {
    key: 'costCentre',
    label: 'Cost centre',
    value: (p) => clean(p.costCentreName) ?? clean(p.costCentreCode),
    identity: (p) => p.costCentreId ?? clean(p.costCentreCode),
  },
  { key: 'workType', label: 'Work type', value: (p) => clean(p.workType) },
  {
    key: 'tags',
    label: 'Tags',
    value: (p) => (p.tags && p.tags.length > 0 ? p.tags.join(', ') : null),
    identity: (p) => (p.tags && p.tags.length > 0 ? [...p.tags].sort().join('|') : null),
  },
];

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

const statusQuerySchema = z.object({
  status: z.enum(['active', 'inactive', 'all']).default('all'),
});

/** Blank optional text from a form arrives as ''; store null, not an empty value. */
const optionalDescription = z
  .string()
  .trim()
  .max(500)
  .transform((value) => (value === '' ? null : value))
  .nullable()
  .optional();

export const createGradeBodySchema = z.object({
  code: z.string().trim().min(1).max(20),
  name: z.string().trim().min(1).max(200),
  rank: z.number().int().min(0).max(10_000).default(0),
  description: optionalDescription,
});

/** The code is immutable once created: assignments mirror it as free text (ADR 0022). */
export const updateGradeBodySchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  rank: z.number().int().min(0).max(10_000).optional(),
  description: optionalDescription,
  isActive: z.boolean().optional(),
});

export const createExitTypeBodySchema = z.object({
  code: z.string().trim().min(1).max(40),
  name: z.string().trim().min(1).max(200),
});

/** The code is immutable once created — Employment.exitType rows already store it verbatim. */
export const updateExitTypeBodySchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  isActive: z.boolean().optional(),
});

export const createCostCentreBodySchema = z.object({
  code: z.string().trim().min(1).max(20),
  name: z.string().trim().min(1).max(200),
  legalEntityId: uuidSchema.nullable().optional(),
  parentId: uuidSchema.nullable().optional(),
  ownerEmploymentId: uuidSchema.nullable().optional(),
});

export const updateCostCentreBodySchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  legalEntityId: uuidSchema.nullable().optional(),
  parentId: uuidSchema.nullable().optional(),
  ownerEmploymentId: uuidSchema.nullable().optional(),
  isActive: z.boolean().optional(),
});

export const createBusinessUnitBodySchema = z.object({
  code: z.string().trim().min(1).max(20),
  name: z.string().trim().min(1).max(200),
  description: optionalDescription,
  legalEntityId: uuidSchema.nullable().optional(),
});

/** The code is immutable once created, like a cost centre's. */
export const updateBusinessUnitBodySchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  description: optionalDescription,
  legalEntityId: uuidSchema.nullable().optional(),
  isActive: z.boolean().optional(),
});

export const createEmployeeGroupBodySchema = z.object({
  name: z.string().trim().min(1).max(200),
  description: optionalDescription,
});

export const updateEmployeeGroupBodySchema = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  description: optionalDescription,
  isActive: z.boolean().optional(),
});

/**
 * The most people ONE "everyone matching this filter" action may touch. A guard against a filter
 * that was left too wide by accident (an empty filter matches the whole company), not a limit on
 * how large a group or cost centre can grow — repeat with a narrower filter to add more.
 */
export const BULK_FILTER_LIMIT = 5000;

/** Most ids one request may carry when the caller picks people by hand. */
export const BULK_IDS_LIMIT = 500;

/**
 * Who to pick. Every field narrows the result; setting none matches every current employee.
 * Only current people match: exited, rescinded and archived employments never do.
 */
export const employmentFilterSchema = z.object({
  /** Matches name, employee code or email; several words must each match. */
  q: z.string().trim().min(1).max(200).optional(),
  legalEntityId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
  /** With `departmentId`: also everyone in that department's sub-departments. */
  includeSubDepartments: z.boolean().optional(),
  locationId: uuidSchema.optional(),
  /** A job title, matched exactly ignoring case — how a Post is stored on an assignment. */
  designation: z.string().trim().min(1).max(200).optional(),
  gradeId: uuidSchema.optional(),
  /** A cost centre id, or `none` for people who are not in any cost centre yet. */
  costCentreId: z.union([uuidSchema, z.literal('none')]).optional(),
  /** Leave out people already in this group — what an "add people" list wants. */
  notInGroupId: uuidSchema.optional(),
  /** Leave out people already in this cost centre. */
  notInCostCentreId: uuidSchema.optional(),
});
export type EmploymentFilter = z.infer<typeof employmentFilterSchema>;

/**
 * A search, sent as a body rather than a query string: it needs `org.settings:update` (it lists
 * people across the whole organisation for someone about to act on them), and this contract gates
 * every GET on the read permission. Nothing is created or changed by it.
 */
export const findEmploymentsBodySchema = employmentFilterSchema.extend({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(25),
});

/** Members lists page and search; the default page is small on purpose — a group can be huge. */
export const membersQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().min(1).max(200).optional(),
});

/** Pick people by hand (`employmentIds`) or by rule (`filter`) — exactly one of the two. */
const selectionRule = (value: { employmentIds?: unknown; filter?: unknown }) =>
  (value.employmentIds === undefined) !== (value.filter === undefined);
const selectionMessage = 'Send either employmentIds or filter, not both and not neither';

export const addEmployeeGroupMembersBodySchema = z
  .object({
    employmentIds: z.array(uuidSchema).min(1).max(BULK_IDS_LIMIT).optional(),
    filter: employmentFilterSchema.optional(),
  })
  .refine(selectionRule, { message: selectionMessage });

export const removeEmployeeGroupMembersBodySchema = z.object({
  employmentIds: z.array(uuidSchema).min(1).max(BULK_IDS_LIMIT),
});

/**
 * Put people in (or take them out of) a cost centre from `effectiveFrom`. It is a dated placement
 * change, like a reassignment — the row in force is closed the day before and a new one opens — so
 * "what cost centre were they in during March" keeps its answer. A change dated the same day the
 * current placement began corrects that row in place (there is no earlier day to close it on).
 * A date before the current placement began is refused for that person.
 */
export const bulkCostCentreBodySchema = z
  .object({
    employmentIds: z.array(uuidSchema).min(1).max(BULK_IDS_LIMIT).optional(),
    filter: employmentFilterSchema.optional(),
    effectiveFrom: dateOnlySchema,
  })
  .refine(selectionRule, { message: selectionMessage });

export const placementHistoryQuerySchema = z.object({
  /** Return only the row in effect on this date — "what grade were they in March". */
  asOf: dateOnlySchema.optional(),
});

const deleteResultSchema = z.object({ success: z.boolean() });
const addMembersResultSchema = z.object({
  /** Newly added; employments already in the group are skipped, not an error. */
  added: z.number().int().nonnegative(),
  /** How many people the request selected (a filter's matches, or the ids sent). */
  matched: z.number().int().nonnegative(),
});
const removeMembersResultSchema = z.object({ removed: z.number().int().nonnegative() });

/** Why some of the selected people were left alone, so a partial result is never a mystery. */
export const bulkCostCentreResultSchema = z.object({
  matched: z.number().int().nonnegative(),
  changed: z.number().int().nonnegative(),
  skipped: z.number().int().nonnegative(),
  skippedBy: z.array(z.object({ reason: z.string(), count: z.number().int().positive() })),
});
export type BulkCostCentreResult = z.infer<typeof bulkCostCentreResultSchema>;

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

const readMeta = routeMeta('org.settings:read');
/** The grade / cost-centre / business-unit pickers: also open to whoever may change an employee's job. */
const pickerMeta = routeMeta(['org.settings:read', 'employment:update']);
const writeMeta = routeMeta('org.settings:update');

export const orgSetupContract = c.router(
  {
    listGrades: {
      method: 'GET',
      path: '/grades',
      query: statusQuerySchema,
      responses: {
        200: z.object({ items: z.array(gradeSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List grades, ordered by rank',
      metadata: pickerMeta,
    },
    createGrade: {
      method: 'POST',
      path: '/grades',
      body: createGradeBodySchema,
      responses: {
        201: gradeSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Create a grade',
      metadata: writeMeta,
    },
    updateGrade: {
      method: 'PATCH',
      path: '/grades/:gradeId',
      body: updateGradeBodySchema,
      responses: {
        200: gradeSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Update a grade (the code cannot change)',
      metadata: writeMeta,
    },
    deleteGrade: {
      method: 'DELETE',
      path: '/grades/:gradeId',
      responses: {
        200: deleteResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Delete a grade — refused once anyone has ever been placed in it',
      metadata: writeMeta,
    },

    listExitTypes: {
      method: 'GET',
      path: '/exit-types',
      query: statusQuerySchema,
      responses: {
        200: z.object({ items: z.array(exitTypeCatalogueSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List offboarding/exit reasons employees.exit can be given',
      metadata: readMeta,
    },
    createExitType: {
      method: 'POST',
      path: '/exit-types',
      body: createExitTypeBodySchema,
      responses: {
        201: exitTypeCatalogueSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Add an exit type',
      metadata: writeMeta,
    },
    updateExitType: {
      method: 'PATCH',
      path: '/exit-types/:exitTypeId',
      body: updateExitTypeBodySchema,
      responses: {
        200: exitTypeCatalogueSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Rename or deactivate an exit type (the code cannot change)',
      metadata: writeMeta,
    },
    deleteExitType: {
      method: 'DELETE',
      path: '/exit-types/:exitTypeId',
      responses: {
        200: deleteResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Delete an exit type — refused once any employment has ever used it',
      metadata: writeMeta,
    },

    listCostCentres: {
      method: 'GET',
      path: '/cost-centres',
      query: statusQuerySchema,
      responses: {
        200: z.object({ items: z.array(costCentreSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List cost centres',
      metadata: pickerMeta,
    },
    createCostCentre: {
      method: 'POST',
      path: '/cost-centres',
      body: createCostCentreBodySchema,
      responses: {
        201: costCentreSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Create a cost centre',
      metadata: writeMeta,
    },
    updateCostCentre: {
      method: 'PATCH',
      path: '/cost-centres/:costCentreId',
      body: updateCostCentreBodySchema,
      responses: {
        200: costCentreSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Update a cost centre (the code cannot change)',
      metadata: writeMeta,
    },
    deleteCostCentre: {
      method: 'DELETE',
      path: '/cost-centres/:costCentreId',
      responses: {
        200: deleteResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Delete a cost centre — refused once anyone has ever been placed in it',
      metadata: writeMeta,
    },
    listCostCentreMembers: {
      method: 'GET',
      path: '/cost-centres/:costCentreId/members',
      query: membersQuerySchema,
      responses: {
        200: paginatedSchema(costCentreMemberSchema),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'The people currently in a cost centre, paged and searchable',
      metadata: readMeta,
    },
    assignCostCentre: {
      method: 'POST',
      path: '/cost-centres/:costCentreId/assign',
      body: bulkCostCentreBodySchema,
      responses: {
        200: bulkCostCentreResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Put many people in a cost centre from a date, picked by hand or by filter',
      metadata: writeMeta,
    },
    unassignCostCentre: {
      method: 'POST',
      path: '/cost-centres/:costCentreId/unassign',
      body: bulkCostCentreBodySchema,
      responses: {
        200: bulkCostCentreResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Take many people out of a cost centre from a date, picked by hand or by filter',
      metadata: writeMeta,
    },

    listBusinessUnits: {
      method: 'GET',
      path: '/business-units',
      query: statusQuerySchema,
      responses: {
        200: z.object({ items: z.array(businessUnitSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List business units',
      metadata: pickerMeta,
    },
    createBusinessUnit: {
      method: 'POST',
      path: '/business-units',
      body: createBusinessUnitBodySchema,
      responses: {
        201: businessUnitSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Create a business unit',
      metadata: writeMeta,
    },
    updateBusinessUnit: {
      method: 'PATCH',
      path: '/business-units/:businessUnitId',
      body: updateBusinessUnitBodySchema,
      responses: {
        200: businessUnitSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Update a business unit (the code cannot change)',
      metadata: writeMeta,
    },
    deleteBusinessUnit: {
      method: 'DELETE',
      path: '/business-units/:businessUnitId',
      responses: {
        200: deleteResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Delete a business unit — refused once anyone has ever been placed in it',
      metadata: writeMeta,
    },

    listEmployeeGroups: {
      method: 'GET',
      path: '/employee-groups',
      query: statusQuerySchema,
      responses: {
        200: z.object({ items: z.array(employeeGroupSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'List employee groups with their member counts',
      metadata: readMeta,
    },
    createEmployeeGroup: {
      method: 'POST',
      path: '/employee-groups',
      body: createEmployeeGroupBodySchema,
      responses: {
        201: employeeGroupSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Create an employee group',
      metadata: writeMeta,
    },
    updateEmployeeGroup: {
      method: 'PATCH',
      path: '/employee-groups/:groupId',
      body: updateEmployeeGroupBodySchema,
      responses: {
        200: employeeGroupSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Rename, describe, or deactivate an employee group',
      metadata: writeMeta,
    },
    deleteEmployeeGroup: {
      method: 'DELETE',
      path: '/employee-groups/:groupId',
      responses: {
        200: deleteResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Delete an employee group and its membership list',
      metadata: writeMeta,
    },
    listEmployeeGroupMembers: {
      method: 'GET',
      path: '/employee-groups/:groupId/members',
      query: membersQuerySchema,
      responses: {
        200: paginatedSchema(employeeGroupMemberSchema),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'The employees in a group, paged and searchable',
      metadata: readMeta,
    },
    addEmployeeGroupMembers: {
      method: 'POST',
      path: '/employee-groups/:groupId/members',
      body: addEmployeeGroupMembersBodySchema,
      responses: {
        200: addMembersResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Add employees to a group, picked by hand or by filter (already-present ones are skipped)',
      metadata: writeMeta,
    },
    removeEmployeeGroupMembers: {
      method: 'POST',
      path: '/employee-groups/:groupId/members/remove',
      body: removeEmployeeGroupMembersBodySchema,
      responses: {
        200: removeMembersResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Remove several employees from a group at once',
      metadata: writeMeta,
    },
    removeEmployeeGroupMember: {
      method: 'DELETE',
      path: '/employee-groups/:groupId/members/:employmentId',
      responses: {
        200: deleteResultSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Remove one employee from a group',
      metadata: writeMeta,
    },

    findEmployments: {
      method: 'POST',
      path: '/employments/find',
      body: findEmploymentsBodySchema,
      responses: {
        200: paginatedSchema(employmentMatchSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        'Find current employees by company, department, branch, job title, grade, cost centre or name — for the bulk "add people" pickers',
      metadata: writeMeta,
    },

    getPlacementHistory: {
      method: 'GET',
      path: '/employments/:employmentId/placements',
      query: placementHistoryQuerySchema,
      responses: {
        200: z.object({ items: z.array(placementSchema) }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "An employee's job placements over time, newest first (or as of one date)",
      metadata: readMeta,
    },
  },
  { pathPrefix: '/organisation' },
);
